import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BusinessEntity, useUpdateBusiness } from "@/hooks/useBusinesses";
import { wizardSchema, WizardValues } from "../wizard/schema";
import { BasicsStep } from "../wizard/BasicsStep";
import { LocationStep } from "../wizard/LocationStep";

interface Props {
  b: BusinessEntity;
}

const seed = (b: BusinessEntity): Partial<WizardValues> => ({
  ownerId: b.ownerId,
  businessName: b.businessName,
  nameEn: b.nameEn,
  nameMl: b.nameMl,
  descriptionEn: b.descriptionEn ?? undefined,
  descriptionMl: b.descriptionMl ?? undefined,
  logoUrl: b.logoUrl ?? undefined,
  address: b.address,
  city: b.city,
  district: b.district ?? undefined,
  pincode: b.pincode ?? "",
  phone: b.phone,
  email: b.email ?? undefined,
  whatsapp: b.whatsapp ?? undefined,
  instagramUrl: b.instagramUrl ?? undefined,
  facebookUrl: b.facebookUrl ?? undefined,
  youtubeUrl: b.youtubeUrl ?? undefined,
  lat: b.lat != null ? Number(b.lat) : undefined,
  lng: b.lng != null ? Number(b.lng) : undefined,
  careTypes: ((b.careTypes as string[]) ?? []).map((c) =>
    c.toLowerCase(),
  ) as any,
  amenityIds: (b.amenities ?? []).map((a) => a.amenityId),
});

export const OverviewTab = ({ b }: Props) => {
  const form = useForm<WizardValues>({
    resolver: zodResolver(wizardSchema),
    mode: "onBlur",
    defaultValues: seed(b),
  });
  const update = useUpdateBusiness();

  useEffect(() => {
    form.reset(seed(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.id]);

  const onSave = form.handleSubmit(async (values) => {
    const { pendingDocuments, pendingPhotos, ...rest } = values;
    const patch = rest as unknown as Partial<BusinessEntity>;
    try {
      await update.mutateAsync({ id: b.id, data: patch });
      toast.success("Business updated");
      form.reset(seed({ ...b, ...(patch as any) }));
    } catch (e: any) {
      toast.error(e?.message ?? "Update failed");
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={onSave} className="space-y-6">
        <section className="bg-card rounded-card border border-border p-5 space-y-4">
          <h3 className="font-semibold">Basics</h3>
          <BasicsStep businessId={b.id} />
        </section>
        <section className="bg-card rounded-card border border-border p-5 space-y-4">
          <h3 className="font-semibold">Location & contact</h3>
          <LocationStep />
        </section>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => form.reset(seed(b))}
            disabled={!form.formState.isDirty || update.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!form.formState.isDirty || update.isPending}
          >
            {update.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
